import { defineStore } from 'pinia'
import type { FormKitSelectOption } from '~/types/forms'
import type { Shift } from '~/types/db/shifts'
import { closingShiftSchema } from '~/schemas/shift'
import type { ShiftResponse, OpenShiftForm, ShiftUsersResponse } from '~/types/shift'
import {
  PublicStoreShiftFormsOpen,
  PublicStoreShiftFormsClose,
} from '#components'
import { usePaymentMethodsStore } from '~/stores/admin/paymentMethods'
import { makeCashNameLookup, hasConfiguredCashMethod, cashSalesFromBreakdown, checksCashTotal, expectedCash, cashDifference } from '~/utils/shiftCash'
import { bookingsPaymentBreakdown as bookingsPaymentBreakdownOf } from '~/utils/shift-report/bookingPayments'
import type { BookingPaymentRow } from '~/utils/shift-report/bookingPayments'

export type ClosingShiftForm = {
  closing_cash_amount: number
  closing_notes: string
  id: number
}

export type PaymentBreakdown = {
  payment_type: string
  amount: number
  order_count: number
  /** Processor commission of counter orders paid by this method; absent before the feature. */
  commission?: number
}

export type ChecksPaymentBreakdown = {
  payment_type: string
  is_cash: boolean
  amount: number
  check_count: number
  /** Processor commission for this method on this shift; absent before the feature. */
  commission?: number
}

export type ShiftSalesResponse = {
  success: boolean
  total: number
  grand_total: number
  payment_breakdown: PaymentBreakdown[]
  checks_payment_breakdown?: ChecksPaymentBreakdown[]
  /** Scheduled-order money that landed on this shift. See utils/shift-report/bookingPayments. */
  bookings_payments?: BookingPaymentRow[]
  commission_total?: number
}

export const useShiftStore = defineStore('shift', () => {
  const log = useLogger('store:shift')
  const { get, post, put, delete: del } = useAuthenticatedFetch()

  // Use business store as single source of truth
  const businessStore = useBusinessStore()
  const { businessId } = storeToRefs(businessStore)
  const paymentMethodsStore = usePaymentMethodsStore()

  // Get nuxtApp inside setup
  const nuxtApp = useNuxtApp()
  const { $dialog, $confirm } = nuxtApp
  const { t } = nuxtApp.$i18n

  // Refs
  const usersOptions: Ref<FormKitSelectOption[] | undefined> = ref(undefined)
  const statusOptions: Ref<{ value: string, label: string }[]> = ref([
    { value: 'open', label: t('open') },
    { value: 'in_progress', label: t('in_progress') },
    { value: 'closed', label: t('closed') },
  ])
  const shift: Ref<Shift | undefined> = ref(undefined)
  const totalSales: Ref<number | undefined> = ref(0)
  const paymentBreakdown: Ref<PaymentBreakdown[]> = ref([])
  const checksPaymentBreakdown: Ref<ChecksPaymentBreakdown[]> = ref([])
  const bookingsPayments: Ref<BookingPaymentRow[]> = ref([])
  /** Processor commission of the whole shift (tenders + booking payments + counter orders). */
  const commissionTotal: Ref<number> = ref(0)
  const totalExpenses: Ref<number | undefined> = ref(0)
  // Cash-only expenses (non-cash sources like cards/transfers don't touch the drawer).
  const cashExpenses: Ref<number | undefined> = ref(0)
  // Cash-only deposits linked to this shift (set by useDepositsStore.fetchDepositsByShift).
  const cashDeposits: Ref<number | undefined> = ref(0)
  const loading: Ref<boolean> = ref(false)
  const closingCashAmount: Ref<string | undefined> = ref()
  const shiftTable: Ref<any[]> = ref([])
  const shifts: Ref<Shift[]> = ref([])
  const permissions: Ref<Record<string, boolean>> = ref({})

  // Computed
  const shiftStatus = computed(() => shift.value?.status ?? 'closed')
  const shiftId: ComputedRef<number | undefined> = computed({
    get: () => shift.value?.id ?? undefined,
    set: (newValue: number | undefined) => {
      if (shift.value) {
        shift.value.id = newValue
      }
    },
  })
  const cashLookup = computed(() => makeCashNameLookup(paymentMethodsStore.paymentMethods))
  const ordersCash = computed(() => cashSalesFromBreakdown(paymentBreakdown.value, cashLookup.value))
  const checksCash = computed(() => checksCashTotal(checksPaymentBreakdown.value))
  // Same is_cash-snapshot shape as the checks rows, so the same total applies.
  // Refunds are negative rows and net out here — cash handed back left the drawer.
  const bookingsPaymentBreakdown = computed(() => bookingsPaymentBreakdownOf(bookingsPayments.value))
  const bookingsCash = computed(() => checksCashTotal(bookingsPaymentBreakdown.value))

  // Adds a total that reports doubt (`ordersCash.cash`, paired with
  // `computedCashUnclassified` below) to two that cannot (`checksCash` and
  // `bookingsCash` — see the RELIABILITY NOTE on `checksCashTotal` in
  // utils/shiftCash.ts). A zero from `computedCashUnclassified` means the
  // orders half is fully resolved; it says nothing about the other two.
  const computedCashSales: ComputedRef<number> = computed(() =>
    ordersCash.value.cash + checksCash.value + bookingsCash.value,
  )

  const computedExpectedCashAmount: ComputedRef<number> = computed(() =>
    expectedCash({
      openingCash: Number(shift?.value?.opening_cash_amount ?? 0),
      cashSales: computedCashSales.value,
      cashDeposits: Number(cashDeposits.value ?? 0),
      // Cash-only expenses — NOT `totalExpenses`, which also includes
      // non-cash spend and would silently overstate the shortfall if swapped in.
      cashExpenses: Number(cashExpenses.value ?? 0),
    }),
  )

  const computedDiffCashAmount: ComputedRef<number> = computed(() => {
    const diff = cashDifference(Number(closingCashAmount.value), computedExpectedCashAmount.value)
    return isNaN(diff) ? 0 : diff
  })

  // How many order rows the answer is not certain about. Surfaced in Task 4 —
  // a cash figure that may be low must say so where the cashier signs off.
  const computedCashUnclassified: ComputedRef<number> = computed(() => ordersCash.value.unclassified)

  // Distinct from `unclassified > 0`: here the whole lookup table was rejected
  // because no method is flagged as cash, so "these methods did not match your
  // configured methods" would be false — they were never consulted.
  const computedCashMethodMissing: ComputedRef<boolean> = computed(() =>
    paymentMethodsStore.paymentMethods.length > 0
    && !hasConfiguredCashMethod(paymentMethodsStore.paymentMethods),
  )

  // Whether the payment-methods catalogue has loaded at all. An empty list
  // has two possible causes that look identical in computedCashUnclassified
  // (every order row falls back to the legacy literal and counts as
  // doubtful): the catalogue genuinely hasn't been fetched yet, or fetchItems
  // failed and swallowed its own error. false here means "the doubt you're
  // seeing may just be an unloaded/failed catalogue, not unrecognized names" —
  // Task 4 needs this to avoid sending a cashier to fix configuration that's
  // actually fine.
  const computedCashMethodsLoaded: ComputedRef<boolean> = computed(() =>
    paymentMethodsStore.paymentMethods.length > 0,
  )

  // Methods
  function reset() {
    // Reset all store state to initial values
    shift.value = undefined
    totalSales.value = 0
    paymentBreakdown.value = []
    checksPaymentBreakdown.value = []
    bookingsPayments.value = []
    commissionTotal.value = 0
    totalExpenses.value = 0
    cashExpenses.value = 0
    cashDeposits.value = 0
    loading.value = false
    businessStore.clearBusinessData()
    closingCashAmount.value = undefined
    shiftTable.value = []
    shifts.value = []
    usersOptions.value = undefined
    permissions.value = {}

    log.info('✅ Shift store reset to initial state')
  }

  // clearBusinessData removed - store disposal handles clean state

  function setUsers(data: FormKitSelectOption[]) {
    if (!data) {
      return
    }

    usersOptions.value = data
  }

  function setShift(data: Shift) {
    shift.value = data
  }

  function endShift() {
    shift.value = undefined
  }

  function clearStore() {
    totalSales.value = 0
    paymentBreakdown.value = []
    checksPaymentBreakdown.value = []
    bookingsPayments.value = []
    commissionTotal.value = 0
    totalExpenses.value = 0
    cashExpenses.value = 0
    cashDeposits.value = 0
    closingCashAmount.value = undefined
    shift.value = undefined
    permissions.value = {}
  }

  async function fetchOrdersByShift(shiftId: number | string): Promise<any[]> {
    try {
      const res = await get<{ success: boolean, data: any[] }>(`/api/business/${businessId.value}/shift/${shiftId}/orders`)
      return res?.data || []
    }
    catch {
      useErrorToast('Failed to load shift orders')
      return []
    }
  }

  async function getShiftSales(shiftId: number) {
    // Guard lives here, not in getAllShiftData, so it applies to every path
    // that populates paymentBreakdown — including createShiftStore's
    // "resume an open shift" branch, which calls this directly and used to
    // bypass the guard entirely.
    if (paymentMethodsStore.paymentMethods.length === 0) {
      await paymentMethodsStore.fetchItems()
    }

    try {
      const res = await get<ShiftSalesResponse>(`/api/business/${businessId.value}/shift/${shiftId}/sales`)

      if (res && res.success) {
        totalSales.value = res.grand_total || res.total || 0
        paymentBreakdown.value = res.payment_breakdown || []
        checksPaymentBreakdown.value = res.checks_payment_breakdown || []
        bookingsPayments.value = res.bookings_payments || []
        commissionTotal.value = Number(res.commission_total ?? 0)
      }
    }
    catch {
      useErrorToast('Failed to fetch shift sales')
    }
  }

  async function fetchShiftExpenses(shiftId: number) {
    // Delegate to expense store
    const expenseStore = useExpenseStore()
    await expenseStore.fetchExpenses(shiftId)
  }

  async function fetchShiftDeposits(shiftId: number) {
    // Delegate to deposits store (sets shiftStore.cashDeposits as a side effect,
    // same pattern as fetchShiftExpenses above)
    const depositsStore = useDepositsStore()
    await depositsStore.fetchDepositsByShift(shiftId)
  }

  async function closeShift(formData: ClosingShiftForm): Promise<void> {
    loading.value = true
    // get total sales.
    // we can attach the shift id to the sale, so we don't have to deal with date handling.
    // get expenses, ins and outs
    const data = {
      ...formData,
      end_timestamp: new Date().toISOString(),
      expected_cash_amount: computedExpectedCashAmount.value,
      cash_difference: computedDiffCashAmount.value,
      status: 'closed',
    }

    const parsedData = closingShiftSchema.safeParse(data)

    if (!parsedData.success) {
      useZodValidatorErrorClient(parsedData, 5000)
    }

    if (shiftStatus.value !== 'open') {
      return
    }

    if (!parsedData.data?.id) {
      return
    }

    try {
      const res = await put(`/api/business/${businessId.value}/shift/${parsedData.data.id}`, parsedData.data)

      if (res.success) {
        endShift()
        // Toast will be shown by the component
      }
    }
    catch {
      useErrorToast('Error closing shift')
    }
    finally {
      loading.value = false
      clearStore()
    }
  }

  async function createShift(formData: OpenShiftForm): Promise<boolean> {
    formData.status = 'open'

    if (formData.status === 'open') {
      try {
        const res = await post<ShiftResponse>(`/api/business/${businessId.value}/shift`, formData)

        if (res.success) {
          setShift(res.data)
          return true
        }
        else {
          // Handle specific error cases
          if (res.error?.code === 'SHIFT_ALREADY_OPEN') {
            const details = res.error.details
            const errorMessage = t('A shift is already open by {userName}', {
              userName: details?.userName || t('Unknown user'),
            })
            useErrorToast(errorMessage)
          }
          else {
            useErrorToast(res.error?.message || t('Failed to open shift'))
          }
          return false
        }
      }
      catch (error) {
        log.error('Failed to open shift:', error)
        useErrorToast('Failed to open shift')
        return false
      }
    }
    return false
  }

  async function getShiftById(shiftId: number):
  Promise<void> {
    try {
      const res = await get<any>(`/api/business/${businessId.value}/shift/${shiftId}`)

      if (res.success) {
        setShift(res.data)
        // Store permissions from response
        if (res.permissions) {
          permissions.value = res.permissions
        }
        // Fetching is an internal operation, no toast needed
      }
      else {
        useErrorToast('Failed to open shift')
      }
    }
    catch (error) {
      log.error('Failed to get shift by ID:', error)
      useErrorToast('Failed to open shift')
    }
  }

  async function fetchOpenShift(): Promise<Shift | null> {
    if (!businessId.value) {
      useErrorToast('Business ID not found')
      return null
    }

    try {
      const res = await get(`/api/business/${businessId.value}/shifts/open`)

      if (res?.success && res.data) {
        setShift(res.data)
        return res.data
      }

      return null
    }
    catch (error) {
      log.error('Failed to fetch open shift:', error)
      useErrorToast('Failed to fetch shift in progress')
      return null
    }
  }

  async function getAllShiftData(shiftId: number | string) {
    const parsedShiftId = typeof shiftId === 'string' ? Number(shiftId) : shiftId

    if (isNaN(parsedShiftId)) {
      throw new Error('Invalid shift ID: must be a valid number')
    }

    // getShiftSales carries its own payment-methods guard, so it runs
    // concurrently with the other three fetches below instead of blocking
    // ahead of them — one fewer round trip on every shift open.
    await Promise.all([
      getShiftById(parsedShiftId),
      fetchShiftExpenses(parsedShiftId),
      fetchShiftDeposits(parsedShiftId),
      getShiftSales(parsedShiftId),
    ])
  }

  async function fetchEmployees(): Promise<void> {
    // Se llama en el top-level del <script setup> de Shift/Wrapper, que está en
    // el header de todas las pantallas del POS. Si la rejección sale de aquí,
    // atraviesa Suspense y tumba la caja entera a la pantalla de error.
    try {
      const res: ShiftUsersResponse = await get(`/api/business/${businessId.value}/shift/users`)

      if (res.success) {
        setUsers(res.data)
      }
      else {
        useErrorToast('Failed to fetch users')
      }
    }
    catch (error) {
      log.error('Failed to fetch employees:', error)
      useErrorToast('Failed to fetch users')
    }
  }

  function openShiftDialog() {
    $dialog.open(PublicStoreShiftFormsOpen, {
      data: {
        businessId: businessId.value,
      },
      props: {
        // Draws its own body and footer sections — see
        // useSectionedDialogSettings for the contract that comes with it.
        header: t('New shift'),
        ...useSectionedDialogSettings,
        position: 'top',
      },
      onClose: async () => {
        // Refresh shifts list after dialog closes
        await fetchItems()
      },
    })
  }

  function closeShiftDialog(shiftData: Shift) {
    shift.value = shiftData
    $dialog.open(PublicStoreShiftFormsClose, {
      data: {
        businessId: businessId.value,
        shift: shiftData,
      },
      props: {
        // See openShiftDialog.
        header: t('Close shift'),
        ...useSectionedDialogSettings,
        position: 'top',
      },
      onClose: async () => {
        // Refresh shifts list after dialog closes
        await fetchItems()
      },
    })
  }

  async function fetchItems(): Promise<void> {
    if (!businessId.value) return

    loading.value = true
    try {
      const res = await get(`/api/business/${businessId.value}/shifts/datatable`)
      shifts.value = Array.isArray(res) ? res : (res?.data || [])
    }
    catch {
      useErrorToast('Failed to load shifts')
    }
    finally {
      loading.value = false
    }
  }

  async function deleteShift(shiftItem: Shift): Promise<boolean> {
    if (!businessId.value) {
      useErrorToast('Business ID not found')
      return false
    }

    return new Promise((resolve) => {
      $confirm.require({
        message: `${t('Shift')} #${shiftItem.id} ${t('will be removed')}`,
        header: t('Delete confirmation'),
        acceptLabel: t('Yes'),
        rejectLabel: t('No'),
        icon: 'pi pi-exclamation-triangle',
        accept: async () => {
          try {
            const res = await del(`/api/business/${businessId.value}/shift/${shiftItem.id}`)

            if (res && !res.success) {
              useErrorToast('Error deleting shift')
              resolve(false)
            }
            else {
              useSuccessToast('Shift removed successfully')
              await fetchItems()
              resolve(true)
            }
          }
          catch (error: unknown) {
            const errorMessage = error instanceof Error ? error.message : 'Network error'
            useErrorToast(t('Failed to delete shift: ') + errorMessage)
            resolve(false)
          }
        },
        reject: () => {
          useErrorToast('Deletion canceled')
          resolve(false)
        },
      })
    })
  }

  function initialize(businessIdParam: string) {
    // Check if switching businesses - if so, reset state
    if (businessId.value && businessId.value !== businessIdParam) {
      reset()
    }

    businessStore.setBusinessId(businessIdParam)
    // Don't automatically fetch all shifts - let components decide when needed
  }

  function dispose() {
    // Clean up store state when switching businesses or logging out
    reset()
  }

  return {
    shift,
    shifts,
    usersOptions,
    statusOptions,
    setUsers,
    setShift,
    endShift,
    shiftId,
    shiftStatus,
    fetchOrdersByShift,
    getShiftSales,
    fetchShiftExpenses,
    fetchShiftDeposits,
    fetchEmployees,
    closeShift,
    createShift,
    getShiftById,
    getAllShiftData,
    businessId,
    totalSales,
    paymentBreakdown,
    checksPaymentBreakdown,
    bookingsPayments,
    bookingsPaymentBreakdown,
    commissionTotal,
    totalExpenses,
    cashExpenses,
    cashDeposits,
    closingCashAmount,
    computedExpectedCashAmount,
    computedDiffCashAmount,
    computedCashSales,
    computedCashUnclassified,
    computedCashMethodMissing,
    computedCashMethodsLoaded,
    loading,
    shiftTable,
    permissions,
    reset,
    openShiftDialog,
    closeShiftDialog,
    fetchItems,
    deleteShift,
    initialize,
    dispose,
    fetchOpenShift,
  }
}, {
  persist: {
    key: 'shift-store',
    storage: localStorage,
    paths: ['shift', 'businessId', 'totalSales', 'paymentBreakdown', 'totalExpenses', 'closingCashAmount', 'permissions'], // Persist critical state
  },
})

export const createShiftStore = async (businessIdParam: string, shiftIdParam?: string) => {
  const shiftStore = useShiftStore()
  const expenseStore = useExpenseStore()
  const depositsStore = useDepositsStore()

  // Initialize store with business ID (handles business switching)
  shiftStore.initialize(businessIdParam)

  // Fetch employees first to ensure usersOptions is populated
  await shiftStore.fetchEmployees()

  // If specific shiftId is provided, load that shift data directly
  if (shiftIdParam) {
    await shiftStore.getAllShiftData(Number(shiftIdParam))
  }
  // If no specific shift requested, check for existing open shift
  else {
    const openShift = await shiftStore.fetchOpenShift()
    // If we have an open shift, load its data
    if (openShift?.id) {
      await Promise.all([
        expenseStore.fetchExpenses(openShift.id),
        depositsStore.fetchDepositsByShift(openShift.id),
        shiftStore.getShiftSales(openShift.id),
      ])
    }
  }

  return shiftStore
}
